import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Instagram, ArrowRight, Shield, Truck, CreditCard } from 'lucide-react';
import { products, contactInfo } from '../mock';
import Footer from '../components/Footer';

export const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_13e5738d-7aa3-4072-b101-9672dafbd61a/artifacts/j65cu3zm_logo.jpg" 
                alt="Gafas La Tri - Logo Oficial" 
                className="h-14 w-auto object-contain"
                loading="eager"
                fetchpriority="high"
                width="120"
                height="56"
              />
            </Link>

            <nav className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-blue-900 font-bold transition-colors">Inicio</Link>
              <Link to="/papa-futbolero" className="text-gray-700 hover:text-blue-900 font-medium transition-colors">
                Día del Padre
              </Link>
              <Link to="/para-mama" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">
                Para Mamá
              </Link>
              <Link to="/aficionado" className="text-gray-700 hover:text-amber-600 font-medium transition-colors">
                Aficionado
              </Link>
              <Link to="/empresas" className="text-gray-700 hover:text-blue-900 font-medium transition-colors" data-testid="nav-empresas">
                Empresas
              </Link>
              <a
                href="https://www.instagram.com/gafasdelatri"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-blue-900 font-medium transition-colors flex items-center gap-2"
              >
                <Instagram size={18} />
                Instagram
              </a>
            </nav>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700 hover:text-blue-900 transition-colors"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200 bg-white">
              <nav className="flex flex-col space-y-3">
                <Link to="/" className="text-blue-900 font-bold py-2">Inicio</Link>
                <Link to="/papa-futbolero" className="text-gray-700 hover:text-blue-900 font-medium py-2">
                  Día del Padre
                </Link>
                <Link to="/para-mama" className="text-gray-700 hover:text-rose-500 font-medium py-2">
                  Para Mamá
                </Link>
                <Link to="/aficionado" className="text-gray-700 hover:text-amber-600 font-medium py-2">
                  Aficionado
                </Link>
                <Link to="/empresas" className="text-gray-700 hover:text-blue-900 font-medium py-2">
                  Empresas
                </Link>
                <a
                  href="https://www.instagram.com/gafasdelatri"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-blue-900 font-medium py-2 flex items-center gap-2"
                >
                  <Instagram size={18} />
                  Instagram
                </a>
              </nav>
            </div>
          )}
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 flex">
            <div className="flex-1 bg-yellow-400"></div>
            <div className="flex-1 bg-blue-700"></div>
            <div className="flex-1 bg-red-600"></div>
          </div>
          
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-red-500 rounded-full blur-3xl"></div>
          </div>

          <div className="container mx-auto px-4 lg:px-8 py-20 lg:py-32 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block bg-yellow-400 text-blue-950 px-5 py-2 rounded-full text-sm font-black tracking-wider mb-8">
                GAFAS OFICIALES DE LA TRI
              </div>
              <h1 className="text-5xl lg:text-7xl font-black mb-6 leading-tight">
                Las gafas oficiales de
                <span className="block mt-2 text-yellow-400">la Selección Ecuatoriana</span>
              </h1>
              <p className="text-xl lg:text-2xl text-blue-100 mb-10 max-w-2xl mx-auto">
                Producto licenciado por la FEF. Protección UV400, diseño premium, y la pasión de La Tri en cada par.
              </p>
              <Link 
                to="/papa-futbolero"
                data-testid="hero-cta-papa-futbolero"
                className="inline-flex items-center gap-3 bg-yellow-400 text-blue-950 px-8 py-4 rounded-xl font-bold text-lg hover:bg-yellow-500 hover:scale-105 transition-all shadow-2xl"
              >
                Ver oferta del Día del Padre
                <ArrowRight size={22} />
              </Link>
            </div>
          </div>
        </section>

        {/* Models Showcase */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">
                Nuestros modelos
              </h2>
              <p className="text-xl text-gray-600">Tres colores que representan la pasión tricolor</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {products.map((product) => (
                <div 
                  key={product.id}
                  data-testid={`home-product-${product.color.toLowerCase()}`}
                  className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                >
                  <div className="aspect-square overflow-hidden bg-gray-50">
                    <img 
                      src={product.image}
                      alt={`Gafas La Tri ${product.name}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="w-6 h-6 rounded-full border-2 border-gray-300"
                        style={{ backgroundColor: product.colorHex }}
                      ></div>
                      <h3 className="text-2xl font-bold text-gray-900">{product.color}</h3>
                    </div>
                    <p className="text-gray-700 mb-6 leading-relaxed">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-3xl font-black text-blue-900">${product.price}</span>
                      <Link
                        to="/papa-futbolero#comprar"
                        className="text-blue-900 hover:text-blue-700 font-bold flex items-center gap-1 transition-all"
                      >
                        Comprar
                        <ArrowRight size={18} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-3xl p-8 text-center shadow-md hover:shadow-xl transition-all">
                <div className="bg-blue-900 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Shield className="text-white" size={32} strokeWidth={2.5} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Producto Oficial</h3>
                <p className="text-gray-600">Licenciado por la Federación Ecuatoriana de Fútbol</p>
              </div>
              <div className="bg-white rounded-3xl p-8 text-center shadow-md hover:shadow-xl transition-all">
                <div className="bg-yellow-400 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Truck className="text-blue-950" size={32} strokeWidth={2.5} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Envío gratis sobre $50</h3>
                <p className="text-gray-600">A todo Ecuador. O retira gratis en Quito.</p>
              </div>
              <div className="bg-white rounded-3xl p-8 text-center shadow-md hover:shadow-xl transition-all">
                <div className="bg-red-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <CreditCard className="text-white" size={32} strokeWidth={2.5} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Pago Seguro</h3>
                <p className="text-gray-600">Tarjeta vía Payphone o pedido por WhatsApp</p>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Campaign */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-5xl mx-auto bg-gradient-to-br from-blue-900 to-blue-800 rounded-3xl overflow-hidden shadow-2xl">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="p-10 lg:p-16 flex flex-col justify-center text-white">
                  <div className="inline-block bg-yellow-400 text-blue-950 px-4 py-2 rounded-full text-xs font-black mb-6 self-start">
                    CAMPAÑA ACTIVA
                  </div>
                  <h2 className="text-3xl lg:text-5xl font-black mb-4 leading-tight">
                    Regala unas gafas a tu papá futbolero
                  </h2>
                  <p className="text-blue-100 text-lg mb-8 leading-relaxed">
                    Diseñamos una promo especial para el papá hincha de La Tri. Combo familiar con envío gratis sobre $50.
                  </p>
                  <Link
                    to="/papa-futbolero"
                    data-testid="featured-cta-papa-futbolero"
                    className="bg-yellow-400 text-blue-950 px-8 py-4 rounded-xl font-bold text-lg hover:bg-yellow-500 hover:scale-105 transition-all inline-flex items-center gap-3 self-start"
                  >
                    Ver oferta
                    <ArrowRight size={22} />
                  </Link>
                </div>
                <div className="relative h-64 lg:h-auto">
                  <img 
                    src="/lifestyle/padre_hijo_celebrando.png"
                    alt="Padre e hijo con Gafas La Tri"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>

            {/* B2B Empresas */}
            <div className="max-w-5xl mx-auto mt-10 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 rounded-3xl overflow-hidden shadow-2xl">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-auto order-2 lg:order-1">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_sunglass-hub-6/artifacts/rmsv3ou9_gafas-de-la-tri-colores-frente.jpg"
                    alt="Gafas La Tri para empresas - regalos corporativos"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-10 lg:p-16 flex flex-col justify-center text-white order-1 lg:order-2">
                  <div className="inline-block bg-yellow-400 text-blue-950 px-4 py-2 rounded-full text-xs font-black mb-6 self-start">
                    PROGRAMA B2B
                  </div>
                  <h2 className="text-3xl lg:text-5xl font-black mb-4 leading-tight">
                    Regalos corporativos con identidad ecuatoriana
                  </h2>
                  <p className="text-slate-200 text-lg mb-8 leading-relaxed">
                    Sorprende a tus clientes y equipos con gafas oficiales de La Tri. Disponibles desde <strong className="text-yellow-400">10 unidades</strong>, con descuentos por volumen hasta <strong className="text-yellow-400">-40%</strong>.
                  </p>
                  <Link
                    to="/empresas"
                    data-testid="featured-cta-empresas"
                    className="bg-yellow-400 text-blue-950 px-8 py-4 rounded-xl font-bold text-lg hover:bg-yellow-500 hover:scale-105 transition-all inline-flex items-center gap-3 self-start"
                  >
                    Solicitar cotización
                    <ArrowRight size={22} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
